import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import AutoModeService, { AutoModeFilters } from '@/services/AutoModeService';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component
export default class AutoModeHotkeyMixin extends Vue {
    protected showAutoModeConfirmModal: boolean = false;
    private isAutoModeNavigating: boolean = false;
    private lastF1PressTime: number = 0;

    /**
     * Handles getting the next Auto Mode item and navigation.
     * Automatically exits Auto Mode if no items are available.
     * @param currentQueueType - Optional current queue type to get next from current position
     * @param currentItemId - Optional current item ID to get next from current position
     * @param filters - Optional filters for queues, stores, and programs
     * @returns true if navigated to next item, false if auto mode exited or error occurred
     */
    protected async handleAutoModeNextItem(
        currentQueueType?: string,
        currentItemId?: string | number | null,
        filters?: AutoModeFilters
    ): Promise<boolean> {
        if (!this.$store.autoModeEnabled) {
            return false;
        }

        if (this.isAutoModeNavigating) {
            console.warn('SD-2299: Auto Mode navigation already in progress, skipping duplicate request');
            return false;
        }

        try {
            this.isAutoModeNavigating = true;

            // If no filters provided (advancing after completion), load saved filters
            let filtersToUse = filters;
            if (!currentQueueType && !currentItemId && filters === undefined) {
                filtersToUse = AutoModeService.getSavedFilters();
            }

            const response = await AutoModeService.getNextItem(
                currentQueueType,
                currentItemId,
                filtersToUse,
                this.$store.autoModeRoutedItemIds,
                this.$store.autoModeRoutedOnlineOrderIds
            );

            this.$store.currentAutoModeQueue = response.queueType || null;

            if (response.itemId && response.routeName) {
                try {
                    await this.$router.replace({
                        name: response.routeName,
                        params: response.routeParams || {},
                        query: response.routeQuery || {}
                    });
                } catch (navigationError) {
                    // Navigating to the item we're already viewing is a no-op, not a
                    // failure. Vue Router rejects it as a duplicated navigation, which
                    // would otherwise bubble up and be shown to the user as an error
                    // toast. Treat it as a successful advance; rethrow anything else.
                    if (!isNavigationFailure(navigationError, NavigationFailureType.duplicated)) {
                        throw navigationError;
                    }
                }
                return true;
            } else {
                // No items available - exit Auto Mode gracefully
                this.$store.autoModeEnabled = false;
                this.$store.currentAutoModeQueue = null;
                this.$store.lastAutoModeResponse = null;
                this.$store.autoModeRoutedItemIds = [];
                this.$store.autoModeRoutedOnlineOrderIds = [];

                this.$notification(NotificationOptions.notificationOptionsPreset(
                    response.message || 'All items completed! Auto Mode has been exited.',
                    NotificationOptions.NotificationTypes.success
                ));
                return false;
            }
        } catch (error) {
            console.error('Error in Auto Mode handling:', error);
            this.$notification(NotificationOptions.error(error));
            return false;
        } finally {
            this.isAutoModeNavigating = false;
        }
    }

    async handleF1Press(): Promise<void> {
        try {
            const now = Date.now();
            const timeSinceLastPress = now - this.lastF1PressTime;
            const DEBOUNCE_MS = 300;

            if (timeSinceLastPress < DEBOUNCE_MS) {
                console.debug(`SD-2299: F1 press debounced (${timeSinceLastPress}ms since last press)`);
                return;
            }

            this.lastF1PressTime = now;

            if (this.isAutoModeNavigating) {
                console.debug('SD-2299: F1 press ignored - navigation already in progress');
                return;
            }

            // Determine if we should get next from current position or from beginning
            if (this.$store.autoModeEnabled && this.$store.lastAutoModeResponse) {
                // Skip to next item from current position (respects saved filters)
                const lastResponse = this.$store.lastAutoModeResponse;
                const savedFilters = AutoModeService.getSavedFilters();
                await this.handleAutoModeNextItem(
                    lastResponse.queueType,
                    lastResponse.itemId,
                    savedFilters
                );
            } else {
                // Enable auto mode before getting first item
                this.$store.autoModeEnabled = true;
                // Fresh session: forget any items routed in a prior session
                this.$store.autoModeRoutedItemIds = [];
                this.$store.autoModeRoutedOnlineOrderIds = [];
                // Get saved filters for fresh auto mode entry
                const savedFilters = AutoModeService.getSavedFilters();
                await this.handleAutoModeNextItem(undefined, undefined, savedFilters);
            }
        } catch (error) {
            console.error('Error in Auto Mode F1 press:', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    onAutoModeConfirmed(filters?: AutoModeFilters): void {
        this.showAutoModeConfirmModal = false;
        this.$store.autoModeEnabled = true;
        // Fresh session: forget any items routed in a prior session
        this.$store.autoModeRoutedItemIds = [];
        this.$store.autoModeRoutedOnlineOrderIds = [];
        this.handleAutoModeNextItem(undefined, undefined, filters);
    }

    onAutoModeCancelled(): void {
        this.showAutoModeConfirmModal = false;
    }
}
